import React from 'react';

import styles from './styles.module.css';

/**
 * Renders children as a block that always occupies the visible intersection
 * of its parent container with the surrounding horizontal scroll viewport.
 *
 * Purpose: when a grid cell spans a wide horizontal range (e.g. a vehicle
 * assignment covering many stations), its inner content (chip, action row,
 * assign panel) should stay visible on-screen while the range is intersected
 * by the scroll viewport — not scroll off with the range.
 *
 * How it works: the root is `position: sticky; left: 0` with
 * `width: 100cqw; max-width: 100%`. `100cqw` refers to the nearest ancestor
 * with `container-type: inline-size` (the shared horizontal Scroller has it).
 * The result: sticky element = min(scroll viewport width, parent cell width),
 * pinned to the viewport's left edge. Children lay out via their own flex/
 * grid rules inside this visible-width band.
 *
 * Stacking: the root sets `z-index: 1`, creating its own stacking context.
 * Tooltips, dropdowns, and action menus rendered inside layer above any
 * non-`GridSticky` siblings (page background, scroll fades, etc.). All
 * `GridSticky` instances share the same z-index, so their stacking order
 * relative to each other follows natural DOM order.
 *
 * CSS contract:
 * - Must be rendered inside `common/Scroller` (or any ancestor with
 *   `container-type: inline-size` on the scroll container).
 * - Parent element defines the horizontal range (e.g. a grid cell with
 *   `grid-column: from/to`). No `position` requirement on the parent.
 * - Children control their own visual (background, padding, alignment).
 * - Do not set a competing `z-index` on peer sticky elements; rely on the
 *   `GridSticky` stacking context and DOM order.
 *
 * @example
 *   <div style={{ gridColumn: '5/11' }}>
 *     <GridSticky>
 *       <div className={styles.plate}>
 *         <Chip>86896</Chip>
 *       </div>
 *     </GridSticky>
 *   </div>
 */
const GridSticky = ({ children }) => (
	<div className={styles.gridSticky} data-testid="grid-sticky">
		{children}
	</div>
);

export default GridSticky;
